import { useReducer } from 'react';
import {
    FriendsIcon,
    QuestionMarkIcon,
    ReferralFeeIcon,
    RewardsIcon,
} from '../../assets/icons';
import Button from '../../components/Button';
import Card from '../../components/Card';
import { formatAmountWithSuffix, formatPercentages } from '../../helpers';
import useUserReferralStats from '../../hooks/referral/useUserReferralStats';
import clsx from 'clsx';
import ExternalLink from '../../components/ExternalLink';

type Tier = {
    tier: string;
    feePercentage: number;
    description: string;
    activeReferralsRequired: number;
    monthlyVolumeRequired: number;
};

const tierList: Tier[] = [
    {
        tier: 'Tier 1',
        feePercentage: 10,
        description: 'standard referral fee',
        activeReferralsRequired: 0,
        monthlyVolumeRequired: 0,
    },
    {
        tier: 'Tier 2',
        feePercentage: 15,
        description: '10+ active referrals, with a total of $5m+ monthly volume',
        activeReferralsRequired: 10,
        monthlyVolumeRequired: 5_000_000,
    },
    {
        tier: 'Tier 3',
        feePercentage: 20,
        description: '15+ active referrals, with a total of $10m+ monthly volume',
        activeReferralsRequired: 15,
        monthlyVolumeRequired: 10_000_000,
    },
    {
        tier: 'Tier 4',
        feePercentage: 25,
        description: '20+ active referrals, with a total of $25m+ monthly volume',
        activeReferralsRequired: 20,
        monthlyVolumeRequired: 25_000_000,
    },
];

const ReferralStats = () => {
    const { userReferralStats } = useUserReferralStats();

    const [showTierList, toggleTierList] = useReducer((prev) => !prev, false);

    const isEligibleForTierUpgrade = () => {
        if (!userReferralStats) return false;

        const currentFee = userReferralStats.referralFeeTier;

        const currentTier = tierList.find(
            ({ feePercentage }) => currentFee < feePercentage
        );

        if (!currentTier) return false;

        const { activeUsersCount, monthlyVolume } =
            userReferralStats.monthtlyReferralStats;

        return (
            activeUsersCount >= currentTier.activeReferralsRequired &&
            +monthlyVolume >= currentTier.monthlyVolumeRequired
        );
    };

    return (
        <div className='referred'>
            <Card>
                <FriendsIcon />
                <div className='total-friends'>
                    <div className='title'>Total Referrals</div>
                    <div className='value'>
                        {userReferralStats ? userReferralStats?.referredCount || 0 : '-'}
                    </div>
                </div>
            </Card>
            <Card>
                <RewardsIcon />
                <div className='total-rewards'>
                    <div className='title'>Total Rewards</div>
                    <div className='value'>
                        {userReferralStats
                            ? formatAmountWithSuffix(
                                  userReferralStats.rewardsEarned || 0,
                                  {
                                      minDecimals: 2,
                                      currency: 'USD',
                                      minValue: 1_000_000,
                                  }
                              )
                            : '-'}
                    </div>
                </div>
            </Card>
            <Card
                className={clsx('referral-fee-card', {
                    'show-tier-list': showTierList,
                })}
            >
                <QuestionMarkIcon tabIndex={0} />
                <div className='referral-fee'>
                    <Card className='referral-fee-card-wrapper'>
                        <ReferralFeeIcon />
                        <div className='current-fee'>
                            <div className='title'>Current referral fee</div>
                            <div className='value'>
                                {userReferralStats
                                    ? `${formatPercentages(
                                          userReferralStats?.referralFeeTier
                                      )}%`
                                    : '-'}
                            </div>
                        </div>
                        <Button className='tier-list-toggle' onClick={toggleTierList}>
                            {showTierList ? 'Back' : 'Tiers'}
                        </Button>
                    </Card>
                    <p>
                        {isEligibleForTierUpgrade() ? (
                            <>
                                You are eligible for a referral tier upgrade! Please
                                contact us to apply,{' '}
                                <ExternalLink to='https://t.me/UnlimitedNetworkChat'>
                                    here
                                </ExternalLink>
                                .
                            </>
                        ) : (
                            `Keep referring users to Unlimited Leverage to increase your
                        referral fee tier up to 25%.`
                        )}
                    </p>
                </div>

                <ul className='tier-list'>
                    {tierList.map(({ tier, description: detail, feePercentage }) => (
                        <li key={tier}>
                            <span className='tier'>{tier}</span>
                            <svg id='line'>
                                <line
                                    x1='0'
                                    y1='1'
                                    x2='100%'
                                    y2='1'
                                    stroke='rgba(255,255,255,0.3)'
                                    strokeDasharray='6'
                                />
                            </svg>
                            <p>
                                <strong>{feePercentage}%</strong>
                                {` - `}
                                <span className='detail'>{detail}</span>
                            </p>
                        </li>
                    ))}
                </ul>
            </Card>
        </div>
    );
};

export default ReferralStats;
